import { GAME_ASSET_SLUG } from "@/config/game";
import { fullIncreaseIcon, partialIncreaseIcon } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { ReactNode } from "react";

/**
 * One upgrade as a chip: what changed and, in the upgrade green, by how much.
 * `capped` draws the half arrow — the stat hit its ceiling before the full
 * bonus landed. `accent` frames a chip that grants something new (a position,
 * a role plus) rather than raising a number.
 */
export const UpgradeChip = ({
  label,
  value,
  showArrow = true,
  capped = false,
  accent = false,
  className,
}: {
  label: ReactNode;
  value?: number | string;
  showArrow?: boolean;
  capped?: boolean;
  accent?: boolean;
  className?: string;
}) => (
  <span
    className={cn(
      "inline-flex h-6 shrink-0 items-center gap-1 rounded-md px-2 text-xs font-semibold whitespace-nowrap text-foreground-body",
      accent ? "border border-upgrade/40 bg-upgrade/10" : "bg-control",
      className,
    )}
  >
    <span>{label}</span>
    {value !== undefined && value !== null && value !== "" && (
      <span className="inline-flex items-center gap-0.5 text-upgrade">
        {capped ? (
          <span className="mb-0.5 inline-flex size-2 items-center justify-center">
            {partialIncreaseIcon}
          </span>
        ) : (
          showArrow && (
            <span className="mb-0.5 inline-flex size-2 items-center justify-center">
              {fullIncreaseIcon}
            </span>
          )
        )}
        {value}
      </span>
    )}
  </span>
);

/** A PlayStyle icon, the "plus" art when the upgrade grants the plus tier. */
export const PlayStyleUpgradeIcon = ({
  playStyleId,
  isPlus,
  className,
}: {
  playStyleId: number;
  isPlus: boolean;
  className?: string;
}) => (
  <img
    className={cn("size-6 shrink-0", className)}
    src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/${GAME_ASSET_SLUG}/${isPlus ? "playstylesplus" : "playstyles"}/${playStyleId}.png`}
    alt={String(playStyleId)}
  />
);
